/**
 * Invoicing top-level routing map — list keys only.
 *
 * THE hand-maintained source of truth for invoicing tab paths. The page
 * registry (`src/modules/invoicing/routing/registry.tsx`) derives its routes
 * from `INVOICING_PAGES`; an `AssertKeysInMap` guard there fails `ts:check` if a
 * registry key has no matching key here, so the two can't drift.
 *
 * Each feature ticket lands ONE list key here + ONE `INVOICING_PAGES` entry.
 * Detail (`/:id`) and create (`/new`) paths are composed from the list path by
 * the registry assembler, so they need no dedicated keys here.
 *
 * Titles are set via `chainWithTitle` so the breadcrumb (`getRouteTitle`) shows
 * a human label instead of the raw path.
 *
 * Tabs still to land (F1/F5/F6, etc.): events / invoices / credit-notes.
 */
import React from 'react'
import { T } from '@transifex/react'

import { pathJoin } from 'src/reducers/routing/utils'

export const createInvoicingRoutesMap = (prefix: string) => ({
  // F1 — Billable Events (LEDGER-616)
  events: pathJoin(prefix)('events').chainMapAuto(
    // Detail (`/:id`) gets its own breadcrumb title so the second segment reads
    // "Event Details" instead of repeating the list name (cf. LEDGER-697).
    (eventsPrefix) => ({
      ':id': (id = ':id') =>
        pathJoin(eventsPrefix)(id).chainWithTitle({
          title: <T _str="Event Details" />,
          breadCrumbTitle: <T _str="Details" />,
        }),
    }),
    {
      title: <T _str="Events" />,
      breadCrumbTitle: <T _str="Events" />,
    }
  ),
  // F2a — Cost Centres (LEDGER-618)
  'cost-centres': pathJoin(prefix)('cost-centres').chainMapAuto(
    // Detail (`/:id`) gets its own breadcrumb title so the second segment reads
    // "Cost centre Details" instead of repeating the list name (LEDGER-699).
    // The `events` child (LEDGER-682) is the linked-events drill-down page.
    (costCentresPrefix) => ({
      ':id': (id = ':id') =>
        pathJoin(costCentresPrefix)(id).chainMapAuto(
          (idPrefix) => ({
            events: pathJoin(idPrefix)('events').chainWithTitle({
              title: <T _str="Linked events" />,
              breadCrumbTitle: <T _str="Linked events" />,
            }),
          }),
          {
            title: <T _str="Cost centre Details" />,
            breadCrumbTitle: <T _str="Details" />,
          }
        ),
    }),
    {
      title: <T _str="Cost centres" />,
      breadCrumbTitle: <T _str="Cost centres" />,
    }
  ),
  // F2b — Accounting Accounts (LEDGER-620)
  'accounting-accounts': pathJoin(prefix)('accounting-accounts').chainMapAuto(
    // Detail (`/:id`) gets its own breadcrumb title so the second segment reads
    // "Accounting account Details" instead of repeating the list name (LEDGER-697).
    // The `events` child (LEDGER-691) is the recent-events drill-down page.
    (accountsPrefix) => ({
      ':id': (id = ':id') =>
        pathJoin(accountsPrefix)(id).chainMapAuto(
          (idPrefix) => ({
            events: pathJoin(idPrefix)('events').chainWithTitle({
              title: <T _str="Recent events" />,
              breadCrumbTitle: <T _str="Recent events" />,
            }),
          }),
          {
            title: <T _str="Accounting account Details" />,
            breadCrumbTitle: <T _str="Details" />,
          }
        ),
    }),
    {
      title: <T _str="Accounting accounts" />,
      breadCrumbTitle: <T _str="Accounting accounts" />,
    }
  ),
  // PRO-48 — Scheduled billing (schedule-driven primary run type)
  'scheduled-billing': pathJoin(prefix)('scheduled-billing').chainWithTitle({
    title: <T _str="Scheduled billing" />,
    breadCrumbTitle: <T _str="Scheduled billing" />,
  }),
  // F5 — Invoices (LEDGER-625)
  // Detail (`/:id`) gets its own breadcrumb title so the second segment reads
  // "Invoice Details" instead of repeating the list name (cf. events).
  invoices: pathJoin(prefix)('invoices').chainMapAuto(
    (invoicesPrefix) => ({
      ':id': (id = ':id') =>
        pathJoin(invoicesPrefix)(id).chainWithTitle({
          title: <T _str="Invoice Details" />,
          breadCrumbTitle: <T _str="Details" />,
        }),
    }),
    {
      title: <T _str="Invoices" />,
      breadCrumbTitle: <T _str="Invoices" />,
    }
  ),
  // D8 — Billing runs (invoice-e2e plan, 2026-07-18)
  'billing-runs': pathJoin(prefix)('billing-runs').chainMapAuto(
    (runsPrefix) => ({
      ':id': (id = ':id') =>
        pathJoin(runsPrefix)(id).chainWithTitle({
          title: <T _str="Billing run Details" />,
          breadCrumbTitle: <T _str="Details" />,
        }),
    }),
    {
      title: <T _str="Billing runs" />,
      breadCrumbTitle: <T _str="Billing runs" />,
    }
  ),
  // PRO-335 — Billing-run simulations (saved dry runs; promote executes a
  // real run). Detail (`/:id`) is the SIMULATION-labelled result page.
  simulations: pathJoin(prefix)('simulations').chainMapAuto(
    (simulationsPrefix) => ({
      ':id': (id = ':id') =>
        pathJoin(simulationsPrefix)(id).chainWithTitle({
          title: <T _str="Simulation Details" />,
          breadCrumbTitle: <T _str="Details" />,
        }),
    }),
    {
      title: <T _str="Simulations" />,
      breadCrumbTitle: <T _str="Simulations" />,
    }
  ),
  // F2c — Routing Rules (LEDGER-685)
  'routing-rules': pathJoin(prefix)('routing-rules').chainWithTitle({
    title: <T _str="Routing rules" />,
    breadCrumbTitle: <T _str="Routing rules" />,
  }),
  // F6 — Credit Notes (LEDGER-627)
  'credit-notes': pathJoin(prefix)('credit-notes').chainMapAuto(
    (creditNotesPrefix) => ({
      ':id': (id = ':id') =>
        pathJoin(creditNotesPrefix)(id).chainWithTitle({
          title: <T _str="Credit note Details" />,
          breadCrumbTitle: <T _str="Details" />,
        }),
    }),
    {
      title: <T _str="Credit notes" />,
      breadCrumbTitle: <T _str="Credit notes" />,
    }
  ),
})
